import React, {useCallback, useRef} from 'react';
import intl from 'react-intl-universal';
import Cropper from 'react-cropper';
import {useAppState, useUserState, useServiceRegistry} from "@stores/AppStateContext";

import { ModalConfirm } from '../../../components/ModalConfirm/ModalConfirm';
import { compressImage } from '../../../../utils/file/compressImage';
import { getExtensionFromMimeType } from '../../../../utils/file/getExtensionFromMimeType';
import { logErrorToSentry } from '../../../../utils/errors/logErrorToSentry';

import { snackBarRef } from '@stores/snackbarRef';
import {MODALS} from "../modal-names";
import {screenSizeProvider} from "@stores/ScreenSizeProvider";

export interface CropModalProps {
    cropperBase: string | null;
    cropperBaseType: string | null;
    fileKeyPart: string;
    saveAvatarPath: (s3FileName: string) => Promise<void>;
}


import "cropperjs/dist/cropper.css";


export const CropModal = (props: CropModalProps) => {
    const appState = useAppState();
    const userState = useUserState();
    const serviceRegistry = useServiceRegistry();
    const storageService = serviceRegistry.getStorageService();

    const cropperRef = useRef<HTMLImageElement | null>(null);
    const activeModal = appState.topModal;
    const handleClose = useCallback(() => {
        appState.setModal(null)
    }, [appState]);
    const isMobile = screenSizeProvider.view !== 'desktop';
    const onSave = async () => {
        const imageElement: any = cropperRef.current;
        const cropper: any = imageElement?.cropper;

        if (!cropper) return;

        return new Promise<void>((resolve) => {
            cropper.getCroppedCanvas().toBlob(async (fileBlob: Blob | null) => {
                if (!fileBlob) {
                    resolve();
                    return;
                }

                const extension = getExtensionFromMimeType(props.cropperBaseType || 'image/jpeg');
                let normalCompressed = compressImage(fileBlob, 80);
                let thumbnailCompressedBlob = await compressImage(fileBlob, 100, 108, 108);

                normalCompressed.then(normalCompressedBlob => {
                    if (normalCompressedBlob) {
                        storageService.upload(
                            new File([normalCompressedBlob], `${props.fileKeyPart}.origin.${extension}`, { type: 'image/jpeg' }),
                            `${props.fileKeyPart}.origin.${extension}`,
                            { contentType: 'image/jpeg', tenantId: appState.userState!.tenantId }
                        ).catch(err => console.error('Upload of normal compressed failed:', err));
                    }
                });

                try {
                    if (!thumbnailCompressedBlob) {
                        logErrorToSentry('avatar upload compression issue');
                        snackBarRef.current!.enqueueSnackbar({
                            variant: 'warning',
                            message: intl.get('common.upload.error').d('Something went wrong while uploading the file, please contact us'),
                        });
                        resolve();
                        return;
                    }

                    const uploadResult = await storageService.upload(
                        new File([thumbnailCompressedBlob], `${props.fileKeyPart}.${extension}`, { type: 'image/jpeg' }),
                        `${props.fileKeyPart}.${extension}`,
                        { contentType: 'image/jpeg', tenantId: appState.userState!.tenantId }
                    );
                    const s3FileName = uploadResult.path.replace(`${appState.userState!.tenantId}/`, ''); // Remove tenant prefix for backward compatibility
                    await props.saveAvatarPath(s3FileName);
                    await userState!.resetCurrentUserAvatarPath();
                    resolve();
                    appState.setModal(null)
                } catch (error) {
                    console.error('Error during upload process:', error);
                    logErrorToSentry(error as Error);
                    snackBarRef.current!.enqueueSnackbar({
                        variant: 'error',
                        message: intl.get('common.upload.error').d('Something went wrong while uploading the file, please contact us'),
                    });
                    resolve();
                }
            }, 'image/png');
        });
    };

    return (
        <ModalConfirm
            open={activeModal === MODALS.CROP}
            header={ intl.get('avatar_upload.crop_title').d('Customize profile picture')}
            onClose={handleClose}
            onCancel={handleClose}
            onConfirm={onSave}
            confirmText={intl.get('avatar_upload.crop_image').d('Save')}
            maxWidth={600}
            minWidth={440}
            reverseButtons={false}
            isDark={false}
            isMobileFullScreen={isMobile}
        >
            <Cropper
                src={props.cropperBase!}
                className={'AvatarUpload__cropper'}
                style={{ width: "100%" }}
                viewMode={2}
                    // Cropper.js options
                aspectRatio={16 / 16}
                background={false}
                guides={true}
                modal={true}
                movable={false}
                rotatable={false}
                ref={cropperRef}
            />
        </ModalConfirm>
    );
};

